import React, { useState } from 'react';
import { Bot, Copy, Check, ExternalLink, Code2, Sparkles, Key } from 'lucide-react';
import { TunnelStatus } from '../types';

interface OpenAIBridgeViewProps {
  status: TunnelStatus | null;
}

export const OpenAIBridgeView: React.FC<OpenAIBridgeViewProps> = ({ status }) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const isClient = typeof window !== 'undefined';
  const isLocalhost = isClient && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const defaultRelay = isLocalhost ? 'http://127.0.0.1:19841' : (isClient ? window.location.origin : 'https://openaitunnel.kookai.cloud');
  const relayUrl = status?.relayUrl ? status.relayUrl.replace('ws://', 'http://').replace('wss://', 'https://') : defaultRelay;
  const httpEndpoint = (!isLocalhost && isClient) ? window.location.origin : relayUrl;
  const tunnelId = status?.tunnelId || 'open-tunnel-local';
  const sseUrl = `${httpEndpoint}/sse?tunnelId=${tunnelId}`;
  const openApiUrl = `${httpEndpoint}/openapi.json?tunnelId=${tunnelId}`;

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '20px' }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Sparkles size={20} color="var(--accent-cyan)" />
          Connect OpenAI / ChatGPT / Custom GPT to Your Machine
        </h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          OpenAITunnel provides both Remote MCP (Model Context Protocol) and OpenAPI 3.1 Custom Actions. You can connect ChatGPT Web, ChatGPT Mobile, Cursor, Claude Desktop, or custom Python agents directly through your outbound tunnel without opening any inbound firewall ports.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '16px' }}>
        {/* Method 1: MCP Server SSE */}
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '0.95rem', color: 'var(--accent-green)' }}>
            <Bot size={18} />
            Option 1: Model Context Protocol (MCP Server SSE)
          </div>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
            Use with Cursor, Claude Desktop, or MCP-compatible OpenAI bridges.
          </p>

          <div>
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>SSE ENDPOINT URL:</span>
            <div className="copy-box" style={{ marginTop: '4px' }}>
              <span className="copy-text">{sseUrl}</span>
              <button className="copy-btn" onClick={() => copyToClipboard(sseUrl, 'sse')}>
                {copiedKey === 'sse' ? <Check size={14} color="var(--accent-green)" /> : <Copy size={14} />}
                {copiedKey === 'sse' ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>

          <div className="code-preview-box" style={{ fontSize: '0.78rem' }}>
{`// Claude Desktop / MCP config.json
{
  "mcpServers": {
    "open-ai-tunnel": {
      "url": "${sseUrl}"
    }
  }
}`}
          </div>
        </div>

        {/* Method 2: ChatGPT Custom GPT Actions (OpenAPI 3.1) */}
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '0.95rem', color: 'var(--accent-cyan)' }}>
            <Code2 size={18} />
            Option 2: ChatGPT Custom GPT Actions (OpenAPI 3.1)
          </div>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
            Import OpenAPI schema directly into ChatGPT's Custom GPT Action editor.
          </p>

          <div>
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>OPENAPI SCHEMA URL:</span>
            <div className="copy-box" style={{ marginTop: '4px' }}>
              <span className="copy-text">{openApiUrl}</span>
              <button className="copy-btn" onClick={() => copyToClipboard(openApiUrl, 'openapi')}>
                {copiedKey === 'openapi' ? <Check size={14} color="var(--accent-green)" /> : <Copy size={14} />}
                {copiedKey === 'openapi' ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>

          <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', background: 'var(--bg-card)', padding: '10px 14px', borderRadius: 'var(--radius-md)' }}>
            <strong>How to setup in ChatGPT:</strong>
            <ol style={{ paddingLeft: '20px', marginTop: '6px', lineHeight: 1.5 }}>
              <li>Open <em>Explore GPTs</em> &rarr; <em>Create a GPT</em> &rarr; <em>Configure</em>.</li>
              <li>Under <em>Actions</em>, click <strong>Create new action</strong>.</li>
              <li>Paste the OpenAPI Schema URL into <strong>Import from URL</strong>.</li>
              <li>Select <em>API Key</em> authentication and set header to <code>X-Tunnel-Key</code>.</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};
